import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Building2, ArrowRight, Gift } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refSlug = params.get("ref");
  const [inviter, setInviter] = useState(null);
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!refSlug) return;
    api.get(`/share/public/${refSlug}`)
      .then((r) => setInviter(r.data))
      .catch(() => {});
  }, [refSlug]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const payload = refSlug ? { ...form, referral_slug: refSlug } : form;
    const res = await register(payload);
    setLoading(false);
    if (res.ok) nav("/app", { replace: true });
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen flex bg-paper" data-testid="register-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-12" data-testid="register-logo">
            <div className="w-8 h-8 rounded-md bg-forest flex items-center justify-center text-white">
              <Building2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <span className="font-heading text-xl font-semibold">BizCashflow <span className="text-forest">Pros</span></span>
          </Link>

          <h1 className="font-heading text-4xl font-medium tracking-tight mb-2">Start free.</h1>
          <p className="text-ink-light mb-6">30 seconds. No credit card.</p>

          {inviter && (
            <div className="card-surface bg-forest/5 border-forest/15 p-4 mb-6 flex items-start gap-3" data-testid="referral-banner">
              <Gift className="w-5 h-5 text-forest mt-0.5 shrink-0" strokeWidth={1.5} />
              <div className="text-sm">
                <div className="font-medium text-ink">{inviter.first_name} invited you</div>
                <div className="text-xs text-ink-light mt-0.5">
                  Sign up and {inviter.first_name} gets a free month — and you get to find your own missed deductions.
                </div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Your name</label>
              <input required value={form.name} onChange={onChange("name")} data-testid="register-name-input"
                className="w-full bg-white border border-border rounded-lg px-3.5 py-2.5 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Business name <span className="text-ink-lighter font-normal">(optional)</span></label>
              <input value={form.business_name} onChange={onChange("business_name")} data-testid="register-business-input"
                className="w-full bg-white border border-border rounded-lg px-3.5 py-2.5 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email</label>
              <input type="email" required value={form.email} onChange={onChange("email")} data-testid="register-email-input"
                className="w-full bg-white border border-border rounded-lg px-3.5 py-2.5 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Password</label>
              <input type="password" minLength={6} required value={form.password} onChange={onChange("password")} data-testid="register-password-input"
                className="w-full bg-white border border-border rounded-lg px-3.5 py-2.5 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none" />
              <div className="text-xs text-ink-lighter mt-1">At least 6 characters.</div>
            </div>
            {err && <div className="text-sm text-destructive" data-testid="register-error">{err}</div>}
            <button type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full btn-forest inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Creating…" : <>Create account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-sm text-ink-light">
            Already have an account? <Link to="/login" className="text-forest font-medium hover:underline" data-testid="link-to-login">Sign in</Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:block lg:w-1/2 bg-forest relative overflow-hidden">
        <div className="absolute inset-0 grain"></div>
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="overline text-forest-50 mb-6">What's waiting inside</div>
          <ul className="space-y-3 text-base text-forest-50 max-w-md">
            <li>— Tax savings tracker with quarterly projections</li>
            <li>— Snowball & avalanche debt timelines</li>
            <li>— Cash runway, real-time</li>
            <li>— AI advisor: Claude Sonnet 4.5 or GPT-5.2</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
